import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import CompanyUserManagement from './CompanyUserManagement';
import BulkTaskManagement from './BulkTaskManagement';
import TaskSettings from '../pages/TaskSettings';
import EmailConfirmationModal from './EmailConfirmationModal';
import StreamLaunchContactCard from './StreamLaunchContactCard';
import { api } from '../utils/api';

const CompanyManagement = ({ userInfo, onCompanySelected, onCompanyContextChanged, onLogout }) => {
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [deletedCompanies, setDeletedCompanies] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    assignedAdmins: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Founder invitation state
  const [initialFounder, setInitialFounder] = useState({ email: '', name: '', designated: true });
  const [additionalFounders, setAdditionalFounders] = useState([]);
  const [skipOnboarding, setSkipOnboarding] = useState(false);

  // Email confirmation modal state
  const [emailConfirmation, setEmailConfirmation] = useState({
    isOpen: false,
    emailData: null,
    onConfirm: null
  });
  const [admins, setAdmins] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  // Per-company status (onboarding state, formation tasks remaining, new grants).
  // Keyed by company name. Populated via /company-formation/admin/companies-status.
  const [companyStatuses, setCompanyStatuses] = useState({});
  // Initialize activeTab from URL param or default to 'companies'
  // 'bulk-tasks' is the tab's old name; links using it still land on Task Management.
  const requestedTab = searchParams.get('tab') || 'companies';
  const initialTab = requestedTab === 'bulk-tasks' ? 'task-management' : requestedTab;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedCompanyForUsers, setSelectedCompanyForUsers] = useState('');
  // Task Management: 'all-companies' is the bulk tool; 'by-company' shows one
  // company's own Task Settings page without having to switch into that company
  // from the companies list first.
  const [taskManagementView, setTaskManagementView] = useState('all-companies');
  const [selectedCompanyForTasks, setSelectedCompanyForTasks] = useState('');
  const [switchingTaskCompany, setSwitchingTaskCompany] = useState(false);
  const [taskCompanyError, setTaskCompanyError] = useState('');
  const [showEditAssignments, setShowEditAssignments] = useState(null);
  const [editingAssignments, setEditingAssignments] = useState([]);
  const [resetEmails, setResetEmails] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingCompany, setEditingCompany] = useState(null);
  const [editCompanyData, setEditCompanyData] = useState({ name: '', assignedAdmins: [] });
  const [savingCompanyEdit, setSavingCompanyEdit] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editAdminData, setEditAdminData] = useState({ name: '', username: '' });
  
  // Template management state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  // Legal template management state
  const [legalTemplates, setLegalTemplates] = useState([]);
  const [selectedLegalTemplate, setSelectedLegalTemplate] = useState(null);
  const [legalTemplateFile, setLegalTemplateFile] = useState(null);
  const [uploadingLegalTemplate, setUploadingLegalTemplate] = useState(false);

  // Template tab management
  const [templateTab, setTemplateTab] = useState('grant'); // 'grant', 'legal', or 'email'

  // Email template management state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);
  const [editingEmailTemplate, setEditingEmailTemplate] = useState(false);
  const [emailTemplateContent, setEmailTemplateContent] = useState({
    subject: '',
    htmlContent: ''
  });
  const [expandedEmailTemplates, setExpandedEmailTemplates] = useState(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());

  // Investor management state
  const [investorRequests, setInvestorRequests] = useState([]);
  const [investorLoading, setInvestorLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', company: '', email: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [investorSubTab, setInvestorSubTab] = useState('requests');
  const [notifySettings, setNotifySettings] = useState([]);
  const [ndaRecords, setNdaRecords] = useState([]);
  const [ndaFilterField, setNdaFilterField] = useState('all');
  const [ndaFilterValue, setNdaFilterValue] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const fetchInvestorRequests = async () => {
    setInvestorLoading(true);
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/requests`);
      const data = await response.json();
      if (response.ok) {
        setInvestorRequests(data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching investor requests:', err);
    } finally {
      setInvestorLoading(false);
    }
  };

  const handleApproveInvestor = async (requestId) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/approve/${requestId}`);
      const data = await response.json();
      if (response.ok) {
        fetchInvestorRequests();
      } else {
        setError(data.error || 'Failed to approve');
      }
    } catch (err) {
      setError('Failed to approve investor request');
    }
  };

  const handleRejectInvestor = async (requestId) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/reject/${requestId}`);
      const data = await response.json();
      if (response.ok) {
        fetchInvestorRequests();
      } else {
        setError(data.error || 'Failed to reject');
      }
    } catch (err) {
      setError('Failed to reject investor request');
    }
  };

  const handleDeleteInvestor = async (requestId, investorName) => {
    if (!window.confirm(`Are you sure you want to delete the investor account for ${investorName}? This cannot be undone.`)) {
      return;
    }
    try {
      const response = await api.delete(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/${requestId}`);
      const data = await response.json();
      if (response.ok) {
        fetchInvestorRequests();
        setSuccess('Investor account deleted');
      } else {
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Failed to delete investor account');
    }
  };

  const handleInviteInvestor = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/invite`, inviteForm);
      const data = await response.json();
      if (response.ok) {
        setShowInviteModal(false);
        setInviteForm({ name: '', company: '', email: '' });
        fetchInvestorRequests();
        setSuccess('Investor invited successfully');
      } else {
        setError(data.error || 'Failed to invite');
      }
    } catch (err) {
      setError('Failed to invite investor');
    } finally {
      setInviteLoading(false);
    }
  };

  const fetchNotifySettings = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/notify-settings`);
      if (response.ok) {
        const data = await response.json();
        setNotifySettings(data.settings || []);
      }
    } catch (err) {
      console.error('Error fetching notify settings:', err);
    }
  };

  const handleToggleNotify = async (adminEmail, currentEnabled) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/notify-settings`, {
        adminEmail,
        enabled: !currentEnabled
      });
      if (response.ok) {
        fetchNotifySettings();
      }
    } catch (err) {
      console.error('Error updating notify settings:', err);
    }
  };

  const fetchNdaRecords = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/ndas?all=true`);
      if (response.ok) {
        const data = await response.json();
        setNdaRecords(data.ndas || []);
      }
    } catch (err) {
      console.error('Error fetching NDA records:', err);
    }
  };

  useEffect(() => {
    fetchCompanies();
    if (userInfo?.accountType === 'superadmin') {
      fetchAdmins();
      fetchTemplates();
      fetchLegalTemplates();
      fetchEmailTemplates();
      fetchDeletedCompanies();
    }
    if (userInfo?.accountType === 'superadmin' || userInfo?.accountType === 'admin') {
      fetchInvestorRequests();
      fetchNotifySettings();
      fetchNdaRecords();
      fetchCompanyStatuses();
    }
  }, [userInfo]);

  // Fetch email templates from database
  const fetchEmailTemplates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/email-templates`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Convert snake_case to camelCase for frontend
        const convertedTemplates = data.templates.map(template => {
          // Variables should already be an array from backend
          // But handle edge cases for robustness
          let variables = [];
          if (template.variables) {
            if (Array.isArray(template.variables)) {
              variables = template.variables;
            } else if (typeof template.variables === 'string') {
              try {
                variables = JSON.parse(template.variables);
              } catch (parseError) {
                console.warn('Error parsing variables for template:', template.template_key, parseError);
                variables = [];
              }
            } else {
              // Single value, wrap in array
              variables = [template.variables];
            }
          }

          return {
            id: template.template_key,
            name: template.name,
            description: template.description,
            category: template.category,
            subject: template.subject,
            htmlContent: template.html_content,
            variables: variables,
            editable: true,
            isSystem: template.is_system
          };
        });
        setEmailTemplates(convertedTemplates);
        // Start with every category collapsed
        setCollapsedCategories(new Set(convertedTemplates.map(t => t.category || 'Uncategorized')));
      } else {
        console.error('Failed to fetch email templates');
        // Fallback to empty array
        setEmailTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
      setEmailTemplates([]);
    }
  };

  // Handle navigation state to set active tab
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/companies`);
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched companies:', data);

        // Filter out empty or unnamed companies as extra safety measure
        const validCompanies = data.filter(company =>
          company &&
          company.name &&
          company.name.trim() !== '' &&
          company.name.trim() !== '[unnamed company]' &&
          company.name.toLowerCase() !== 'unnamed company'
        );

        setCompanies(validCompanies);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to fetch companies');
    }
  };

  const fetchCompanyStatuses = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/companies-status`);
      if (response.ok) {
        const data = await response.json();
        const map = {};
        (data.statuses || []).forEach(s => { map[s.companyName] = s; });
        setCompanyStatuses(map);
      }
    } catch (error) {
      console.error('Error fetching company statuses:', error);
    }
  };


  const fetchAdmins = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/admins`);
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins || data); // Handle both old and new API format
        setPendingInvitations(data.pendingInvitations || []);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/templates`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        throw new Error('Failed to fetch templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to fetch templates');
    }
  };

  const handleTemplateDownload = async (templateId) => {
    try {
      const exampleResponse = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/example-documents/${templateId}`);
      
      if (exampleResponse.ok) {
        const data = await exampleResponse.json();
        if (data.success && data.url) {
          // Create a temporary link to download the file
          const link = document.createElement('a');
          link.href = data.url;
          link.download = ''; // Let browser determine filename
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          setError('Template file not available. Please upload a new template file.');
        }
      } else {
        setError('Failed to download template');
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template');
    }
  };

  const handleTemplateUpload = async (templateId, templateName, file) => {
    try {
      setUploadingTemplate(true);
      setError('');
      setSuccess('');
  
      const formData = new FormData();
      formData.append('template', file);
      formData.append('templateName', templateName);
      formData.append('documentId', templateId);
  
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/templates/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
  
      const data = await response.json();
  
      if (response.ok) {
        setSuccess(`Template "${templateName}" updated successfully!`);
        fetchTemplates(); // Refresh templates list
        setSelectedTemplate(null);
        setTemplateFile(null);
      } else {
        setError(data.error || 'Failed to update template');
      }
    } catch (error) {
      console.error('Error uploading template:', error);
      setError('Failed to upload template');
    } finally {
      setUploadingTemplate(false);
    }
  };

  // Legal template management functions
  const fetchLegalTemplates = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/legal-templates`);
      if (response.ok) {
        const data = await response.json();
        setLegalTemplates(data.templates || []);
      } else {
        throw new Error('Failed to fetch legal templates');
      }
    } catch (error) {
      console.error('Error fetching legal templates:', error);
      setError('Failed to fetch legal templates');
    }
  };

  const handleLegalTemplateDownload = async (templateId) => {
    try {
      const exampleResponse = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/legal-documents/${templateId}`);

      if (exampleResponse.ok) {
        const data = await exampleResponse.json();

        if (data.url) {
          const link = document.createElement('a');
          link.href = data.url;
          link.download = '';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          setError('Legal template file not available. Please upload a new template file.');
        }
      } else {
        setError('Failed to download legal template');
      }
    } catch (error) {
      console.error('Error downloading legal template:', error);
      setError('Failed to download legal template');
    }
  };

  const handleLegalTemplateUpload = async (templateId, templateName, file) => {
    try {
      setUploadingLegalTemplate(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('template', file);
      formData.append('templateName', templateName);
      formData.append('documentId', templateId);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/legal-templates/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Legal template "${templateName}" updated successfully!`);
        fetchLegalTemplates(); // Refresh legal templates list
        setSelectedLegalTemplate(null);
        setLegalTemplateFile(null);
      } else {
        setError(data.error || 'Failed to update legal template');
      }
    } catch (error) {
      console.error('Error uploading legal template:', error);
      setError('Failed to upload legal template');
    } finally {
      setUploadingLegalTemplate(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getAssignedAdmins = (companyName) => {
    return admins.filter(admin => 
      admin.assignedCompanies && admin.assignedCompanies.includes(companyName)
    );
  };

  const sortedCompanies = [...companies].sort((a, b) => {
    if (sortField === 'name') {
      const comparison = a.name.localeCompare(b.name);
      return sortDirection === 'asc' ? comparison : -comparison;
    } else if (sortField === 'assignedAdmins') {
      const aAdmins = getAssignedAdmins(a.name);
      const bAdmins = getAssignedAdmins(b.name);
      
      // Get the first admin's email/username for sorting
      const aFirstAdmin = aAdmins.length > 0 ? aAdmins[0].username : '';
      const bFirstAdmin = bAdmins.length > 0 ? bAdmins[0].username : '';
      
      // Companies with no admins go to the end
      if (aFirstAdmin === '' && bFirstAdmin === '') return 0;
      if (aFirstAdmin === '') return 1;
      if (bFirstAdmin === '') return -1;
      
      const comparison = aFirstAdmin.localeCompare(bFirstAdmin);
      return sortDirection === 'asc' ? comparison : -comparison;
    }
    return 0;
  });


  const handleDeleteCompany = async (companyName) => {
    const displayName = companyName || '[Unnamed Company]';
    if (!window.confirm(`Are you sure you want to delete company "${displayName}"? This will move it to deleted companies and remove all admin assignments. Associated non-admin users will also be marked as deleted.`)) {
      return;
    }

    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/delete-company`, 
        { companyName }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Company ${displayName} moved to deleted companies!`);
        fetchCompanies();
        fetchDeletedCompanies();
        if (userInfo?.accountType === 'superadmin') {
          fetchAdmins();
        }
      } else {
        setError(data.error || 'Failed to delete company');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      setError('Failed to delete company');
    }
  };

  const fetchDeletedCompanies = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/deleted-companies`);
      const data = await response.json();
      if (response.ok) {
        setDeletedCompanies(data);
      } else {
        console.error('Error fetching deleted companies:', data.error);
      }
    } catch (error) {
      console.error('Error fetching deleted companies:', error);
    }
  };

  const handleRestoreCompany = async (companyName) => {
    if (!window.confirm(`Are you sure you want to restore company "${companyName}"? This will reactivate the company and its associated users.`)) {
      return;
    }

    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/restore-company`, 
        { companyName }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Company ${companyName} restored successfully!`);
        fetchCompanies();
        fetchDeletedCompanies();
      } else {
        setError(data.error || 'Failed to restore company');
      }
    } catch (error) {
      console.error('Error restoring company:', error);
      setError('Failed to restore company');
    }
  };

  const handlePermanentlyDeleteCompany = async (companyName) => {
    if (!window.confirm(`⚠️ WARNING: This will PERMANENTLY delete company "${companyName}" and ALL associated data including files and folders. This action CANNOT be undone! Are you absolutely sure?`)) {
      return;
    }

    if (!window.confirm(`Final confirmation: Type "DELETE" to confirm permanent deletion of ${companyName}`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/permanently-delete-company`, 
        { companyName }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Company ${companyName} permanently deleted with all data!`);
        fetchDeletedCompanies();
      } else {
        setError(data.error || 'Failed to permanently delete company');
      }
    } catch (error) {
      console.error('Error permanently deleting company:', error);
      setError('Failed to permanently delete company');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (adminUsername) => {
    if (!window.confirm(`Are you sure you want to delete admin user "${adminUsername}"? This will remove all their company assignments.`)) {
      return;
    }

    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/delete-admin`, 
        { adminUsername }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Admin ${adminUsername} deleted successfully!`);
        fetchAdmins();
      } else {
        setError(data.error || 'Failed to delete admin');
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      setError('Failed to delete admin');
    }
  };

  const handleSaveAssignments = async () => {
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/update-admin-assignments`, 
        { 
          adminUsername: showEditAssignments,
          assignedCompanies: editingAssignments 
        }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Admin assignments updated successfully!`);
        setShowEditAssignments(null);
        setEditingAssignments([]);
        fetchAdmins();
      } else {
        setError(data.error || 'Failed to update assignments');
      }
    } catch (error) {
      console.error('Error updating assignments:', error);
      setError('Failed to update assignments');
    }
  };

  const toggleCompanyAssignment = (companyName) => {
    setEditingAssignments(prev => {
      if (prev.includes(companyName)) {
        return prev.filter(c => c !== companyName);
      } else {
        return [...prev, companyName];
      }
    });
  };

  const handleResetUserAccounts = async () => {
    if (!resetEmails.trim()) {
      setError('Please enter at least one email address');
      return;
    }

    const emailList = resetEmails.split('\n').map(email => email.trim()).filter(email => email);
    
    if (emailList.length === 0) {
      setError('Please enter valid email addresses');
      return;
    }

    const confirmMessage = `Are you sure you want to reset accounts for these ${emailList.length} email(s)?\n\n${emailList.join('\n')}\n\nThis will permanently delete all user data including:\n- User accounts from database\n- Email verifications\n- Account metadata\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setResetLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/reset-user-accounts`, 
        { emails: emailList }
      );

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(`Successfully reset ${data.deletedCount} account(s). ${data.errors ? `Errors: ${data.errors.join(', ')}` : ''}`);
        setResetEmails('');
        setShowResetModal(false);
      } else {
        setError(data.error || 'Failed to reset user accounts');
      }
    } catch (error) {
      console.error('Error resetting user accounts:', error);
      setError('Failed to reset user accounts');
    }

    setResetLoading(false);
  };


  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate assigned admins
    if (!newCompany.assignedAdmins || newCompany.assignedAdmins.length === 0) {
      setError('At least one admin must be assigned to the company');
      return;
    }

    if (!skipOnboarding) {
      // Validate initial founder is required
      if (!initialFounder.email || !initialFounder.name) {
        setError('Initial founder email and name are required');
        return;
      }

      // Validate founder emails
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (initialFounder.email && !emailRegex.test(initialFounder.email)) {
        setError('Invalid initial founder email address');
        return;
      }

      for (const founder of additionalFounders) {
        if (founder.email && !emailRegex.test(founder.email)) {
          setError(`Invalid email address for ${founder.name || 'additional founder'}`);
          return;
        }
      }
    }

    // Build recipients list for email confirmation
    const recipients = [];
    if (!skipOnboarding) {
      if (initialFounder.email) {
        recipients.push({
          email: initialFounder.email,
          name: initialFounder.name || 'Initial Founder',
          role: 'Designated to fill out formation form'
        });
      }
      additionalFounders.forEach(founder => {
        if (founder.email) {
          recipients.push({
            email: founder.email,
            name: founder.name || 'Additional Founder',
            role: 'Personal info only (invited later)'
          });
        }
      });
    }

    // Prepare company data with founders
    const companyDataWithFounders = {
      ...newCompany,
      skipOnboarding,
      founders: skipOnboarding ? null : {
        initial: initialFounder.email ? initialFounder : null,
        additional: additionalFounders.filter(f => f.email)
      }
    };

    // Show email confirmation modal if there are founders to invite
    if (recipients.length > 0) {
      setEmailConfirmation({
        isOpen: true,
        emailData: {
          subject: `Invitation to ${newCompany.name} - Company Formation`,
          recipients,
          description: `Creating company "${newCompany.name}" and sending founder invitations`,
          additionalInfo: 'Disclaimer: Additional founders can be added later through user management.'
        },
        onConfirm: () => submitCompanyCreation(companyDataWithFounders)
      });
    } else {
      // No founders to invite (or skip onboarding), create company directly
      await submitCompanyCreation(companyDataWithFounders);
    }
  };

  const submitCompanyCreation = async (companyDataWithFounders) => {
    setLoading(true);
    setEmailConfirmation(prev => ({ ...prev, isOpen: false }));

    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/create-company`,
        { companyData: companyDataWithFounders }
      );

      const data = await response.json();

      if (response.ok) {
        const successMessage = data.message || 'Company created successfully!';
        setSuccess(successMessage);
        setNewCompany({ name: '', assignedAdmins: [] });
        setInitialFounder({ email: '', name: '', designated: true });
        setAdditionalFounders([]);
        setSkipOnboarding(false);
        setShowCreateForm(false);
        fetchCompanies(); // Refresh the list
      } else {
        setError(data.error || 'Failed to create company');
      }
    } catch (error) {
      console.error('Error creating company:', error);
      setError('Failed to create company');
    }

    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCompany(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddAdditionalFounder = () => {
    setAdditionalFounders(prev => [...prev, { email: '', name: '' }]);
  };

  const handleRemoveAdditionalFounder = (index) => {
    setAdditionalFounders(prev => prev.filter((_, i) => i !== index));
  };

  const handleAdditionalFounderChange = (index, field, value) => {
    setAdditionalFounders(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSelectCompany = async (companyName) => {
    console.log('Selecting company:', companyName);
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/select-company`, 
        { companyName }
      );

      const data = await response.json();
      if (data.success) {
        console.log('Company selected successfully, calling callback');
        // Call the callback to update parent state and navigate
        if (onCompanySelected) {
          onCompanySelected(companyName);
        }
      } else {
        setError(data.error || 'Failed to select company');
      }
    } catch (error) {
      console.error('Error selecting company:', error);
      setError('Failed to select company');
    }
  };

  // Task Settings talks to /admin-tasks, which always acts on the company the
  // session is working in. Picking a company here switches that context first so
  // the embedded page reads and writes the right company's tasks, and tells the
  // app so the rest of the UI agrees about which company is selected.
  const handleSelectCompanyForTasks = async (companyName) => {
    setTaskCompanyError('');

    if (!companyName) {
      setSelectedCompanyForTasks('');
      return;
    }

    setSwitchingTaskCompany(true);
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/select-company`,
        { companyName }
      );
      const data = await response.json();

      if (!response.ok || !data.success) {
        setSelectedCompanyForTasks('');
        setTaskCompanyError(data.error || 'Failed to switch to that company');
        return;
      }

      setSelectedCompanyForTasks(companyName);
      if (onCompanyContextChanged) {
        onCompanyContextChanged(companyName);
      }
    } catch (error) {
      console.error('Error switching company for task management:', error);
      setSelectedCompanyForTasks('');
      setTaskCompanyError('Failed to switch to that company');
    } finally {
      setSwitchingTaskCompany(false);
    }
  };

  const handleEditCompany = (company) => {
    const assignedAdmins = getAssignedAdmins(company.name);
    setEditingCompany(company.name);
    setEditCompanyData({
      // The editable name is the company's display name, the same field the
      // Company Info page edits. company.name is the storage key and stays put.
      name: company.officialName || company.name,
      assignedAdmins: assignedAdmins.map(admin => admin.username)
    });
  };

  const handleSaveCompanyEdit = async () => {
    const newName = editCompanyData.name.trim();
    if (!newName) {
      setError('Company name cannot be empty');
      return;
    }

    try {
      setSavingCompanyEdit(true);
      setError('');
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/update-company`, 
        { 
          oldCompanyName: editingCompany,
          newCompanyName: newName,
          assignedAdmins: editCompanyData.assignedAdmins
        }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Company updated successfully!`);
        setEditingCompany(null);
        setEditCompanyData({ name: '', assignedAdmins: [] });
        fetchCompanies();
        fetchAdmins();
      } else {
        setError(data.error || 'Failed to update company');
      }
    } catch (error) {
      console.error('Error updating company:', error);
      setError('Failed to update company');
    } finally {
      setSavingCompanyEdit(false);
    }
  };

  const toggleAdminAssignment = (adminUsername) => {
    setEditCompanyData(prev => ({
      ...prev,
      assignedAdmins: prev.assignedAdmins.includes(adminUsername)
        ? prev.assignedAdmins.filter(a => a !== adminUsername)
        : [...prev.assignedAdmins, adminUsername]
    }));
  };


  const handleRoleChange = async (adminUsername, newRole) => {
    const roleDisplayName = newRole === 'superadmin' ? 'Super Admin' : 'Admin';

    if (!window.confirm(`Are you sure you want to change "${adminUsername}"'s role to ${roleDisplayName}?`)) {
      return;
    }

    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/update-admin-role`,
        { adminUsername, newRole }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccess(`${adminUsername}'s role has been updated to ${roleDisplayName} successfully!`);
        fetchAdmins();
      } else {
        setError(data.error || 'Failed to update admin role');
      }
    } catch (error) {
      console.error('Error updating admin role:', error);
      setError('Failed to update admin role');
    }
  };

  const handleSoAorChange = async (companyName, soAor) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/update-company-so-aor`, {
        companyName,
        soAor
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(`SO/AOR updated successfully for ${companyName}!`);
        fetchCompanies();
      } else {
        setError(data.error || 'Failed to update SO/AOR');
      }
    } catch (error) {
      console.error('Error updating SO/AOR:', error);
      setError('Failed to update SO/AOR');
    }
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin.username);
    setEditAdminData({ 
      name: admin.name || admin.username,
      username: admin.username 
    });
    setEditingAssignments(admin.assignedCompanies || []);
  };

  const handleSaveAdmin = async () => {
    if (!editAdminData.name.trim()) {
      setError('Name cannot be empty');
      return;
    }

    try {
      // Update admin name
      const nameResponse = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/update-admin-name`, {
        adminUsername: editAdminData.username,
        newName: editAdminData.name.trim()
      });

      if (!nameResponse.ok) {
        const nameData = await nameResponse.json();
        setError(nameData.error || 'Failed to update admin name');
        return;
      }

      // Update company assignments
      const assignmentResponse = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/update-admin-assignments`, {
        adminUsername: editAdminData.username,
        assignedCompanies: editingAssignments
      });

      if (!assignmentResponse.ok) {
        const assignmentData = await assignmentResponse.json();
        setError(assignmentData.error || 'Failed to update admin assignments');
        return;
      }

      setSuccess('Admin updated successfully!');
      setEditingAdmin(null);
      setEditAdminData({ name: '', username: '' });
      setEditingAssignments([]);
      fetchAdmins(); // Refresh the admin list
    } catch (error) {
      console.error('Error updating admin:', error);
      setError('Failed to update admin');
    }
  };

  const handleCancelEditAdmin = () => {
    setEditingAdmin(null);
    setEditAdminData({ name: '', username: '' });
    setEditingAssignments([]);
  };

  const handleResendInvitation = async (email) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/resend-admin-invitation`, {
        email
      });

      if (response.ok) {
        setSuccess('Admin invitation resent successfully!');
        fetchAdmins(); // Refresh the list
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to resend invitation');
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError('Failed to resend invitation');
    }
  };

  const handleDeleteInvitation = async (email) => {
    if (!window.confirm(`Are you sure you want to delete the invitation for ${email}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/delete-admin-invitation`, {
        email
      });

      if (response.ok) {
        setSuccess('Admin invitation deleted successfully!');
        fetchAdmins(); // Refresh the list
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete invitation');
      }
    } catch (error) {
      console.error('Error deleting invitation:', error);
      setError('Failed to delete invitation');
    }
  };

  // Only show for admin and super admin users
  if (userInfo?.accountType !== 'admin' && userInfo?.accountType !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-center text-gray-600">Company management is only available for admin users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="card p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {userInfo?.accountType === 'superadmin' ? 'Super Admin Dashboard' : 'Admin Dashboard'}
              </h2>
              <p className="text-gray-600">Welcome, {userInfo?.name || userInfo?.username}! Manage companies, users, and system settings.</p>
            </div>
            <button 
              onClick={onLogout}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Logout
            </button>
          </div>
        </div>

        <StreamLaunchContactCard />

        {/* Navigation Tabs */}
        <div className="card">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('companies')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'companies' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Companies
              </button>
              {userInfo?.accountType === 'superadmin' && (
                <button
                  onClick={() => setActiveTab('deleted-companies')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'deleted-companies' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Deleted Companies ({deletedCompanies.length})
                </button>
              )}
              <button
                onClick={() => setActiveTab('user-management')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'user-management' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                User Management (temporary - for testing only)
              </button>
              <button
                onClick={() => setActiveTab('task-management')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'task-management'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Task Management
              </button>
              {userInfo?.accountType === 'superadmin' && (
                <>
                  <button
                    onClick={() => setActiveTab('admins')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'admins'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Admin Management
                  </button>
                  <button
                    onClick={() => setActiveTab('templates')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ml-8 ${
                      activeTab === 'templates'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Template Management
                  </button>
                </>
              )}
              <button
                onClick={() => setActiveTab('investors')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'investors'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Investor Management
                {investorRequests.filter(r => r.status === 'pending').length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">
                    {investorRequests.filter(r => r.status === 'pending').length}
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>
        
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <>
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Companies</h3>
              {userInfo?.accountType === 'superadmin' && (
                <button 
                  onClick={() => setShowCreateForm(!showCreateForm)}
                  className="btn-primary"
                >
                  {showCreateForm ? 'Cancel' : 'Create New Company'}
                </button>
              )}
            </div>

        {showCreateForm && (
          <div className="card p-6 animate-fade-in">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Company</h3>
            <form onSubmit={handleCreateCompany} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={newCompany.name}
                  onChange={handleInputChange}
                  required
                  className="input-field"
                  placeholder="Enter company name"
                />
              </div>

              {/* Assigned Admins Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned Admins * (At least 1 required)
                </label>
                <div className="space-y-2 mt-2">
                  {admins.length === 0 ? (
                    <p className="text-sm text-gray-500">No admins available. Please create admins first.</p>
                  ) : (
                    admins.map((admin) => (
                      <label key={admin.username} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={newCompany.assignedAdmins.includes(admin.username)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewCompany(prev => ({
                                ...prev,
                                assignedAdmins: [...prev.assignedAdmins, admin.username]
                              }));
                            } else {
                              setNewCompany(prev => ({
                                ...prev,
                                assignedAdmins: prev.assignedAdmins.filter(u => u !== admin.username)
                              }));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{admin.name} ({admin.username})</span>
                      </label>
                    ))
                  )}
                </div>
                {newCompany.assignedAdmins.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Please select at least one admin</p>
                )}
              </div>

              {/* Skip Onboarding Toggle */}
              <div className="border-t border-gray-200 pt-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={skipOnboarding}
                    onChange={(e) => setSkipOnboarding(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Skip Onboarding</span>
                    <p className="text-xs text-gray-500">Create company without founders - they can be added later. No onboarding form or steps required.</p>
                  </div>
                </label>
              </div>

              {/* Initial Founder Section */}
              {!skipOnboarding && (<>
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-semibold text-gray-900">Founder 1 (Required)</h4>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Designated to fill out form
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  The initial founder will be invited to complete the company formation form.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Founder Email *
                    </label>
                    <input
                      type="email"
                      value={initialFounder.email}
                      onChange={(e) => setInitialFounder(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className="input-field"
                      placeholder="founder@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Founder Name *
                    </label>
                    <input
                      type="text"
                      value={initialFounder.name}
                      onChange={(e) => setInitialFounder(prev => ({ ...prev, name: e.target.value }))}
                      required
                      className="input-field"
                      placeholder="Full name"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Founders Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-semibold text-gray-900">Additional Founders (Optional)</h4>
                  <button
                    type="button"
                    onClick={handleAddAdditionalFounder}
                    className="inline-flex items-center px-3 py-1.5 border border-blue-600 text-sm font-medium rounded text-blue-600 bg-white hover:bg-blue-50 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Founder
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Additional founders can only complete their personal info (as if invited later through the form).
                </p>

                {additionalFounders.length === 0 ? (
                  <div className="text-center py-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-sm text-gray-500">No additional founders added yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {additionalFounders.map((founder, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">Founder {index + 2}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveAdditionalFounder(index)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="space-y-2">
                          <input
                            type="email"
                            value={founder.email}
                            onChange={(e) => handleAdditionalFounderChange(index, 'email', e.target.value)}
                            className="input-field"
                            placeholder="Email"
                          />
                          <input
                            type="text"
                            value={founder.name}
                            onChange={(e) => handleAdditionalFounderChange(index, 'name', e.target.value)}
                            className="input-field"
                            placeholder="Full name"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Disclaimer:</strong> Additional founders can be added later through user management.
                  </p>
                </div>
              </div>
              </>)}

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading}
                  className={`btn-primary flex-1 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Creating...' : 'Create Company'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewCompany({ name: '', assignedAdmins: [] });
                    setInitialFounder({ email: '', name: '', designated: true });
                    setAdditionalFounders([]);
                    setSkipOnboarding(false);
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="card overflow-hidden">
          {companies.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No companies found. Create a company first to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                      Company Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('assignedAdmins')}>
                      Assigned Admins {sortField === 'assignedAdmins' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SO/AOR</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedCompanies.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">
                        <div className="flex flex-col items-center">
                          <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <p className="text-lg font-medium text-gray-900 mb-2">No companies found</p>
                          <p className="text-gray-500">
                            {userInfo?.accountType === 'superadmin' 
                              ? 'Create your first company to get started.'
                              : 'No companies have been assigned to you yet.'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sortedCompanies.map((company, index) => {
                      const assignedAdmins = getAssignedAdmins(company.name);
                      return (
                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {company.officialName || company.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {assignedAdmins.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {assignedAdmins.map((admin, idx) => (
                                  <span
                                    key={idx}
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      admin.accountType === 'superadmin'
                                        ? 'bg-purple-100 text-purple-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}
                                  >
                                    {admin.name || admin.username} {admin.accountType === 'superadmin' && '(Super)'}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">No admins assigned</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {userInfo?.accountType === 'superadmin' ? (
                              <select
                                value={company.soAor || ''}
                                onChange={(e) => handleSoAorChange(company.name, e.target.value)}
                                className="text-xs px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select SO/AOR</option>
                                {admins.map((admin) => (
                                  <option key={admin.username} value={admin.username}>
                                    {admin.name || admin.username}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              company.soAor ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {admins.find(a => a.username === company.soAor)?.name || company.soAor}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">Not assigned</span>
                              )
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {(() => {
                              const status = companyStatuses[company.name];
                              if (!status) {
                                return <span className="text-xs text-gray-400 italic">Loading...</span>;
                              }
                              const skippedSetupDone = status.onboardingStatus === 'skipped' &&
                                typeof window !== 'undefined' &&
                                !!localStorage.getItem(`onboardingSetup_${company.name}_completedAt`);
                              const onboardingBadge = (status.onboardingStatus === 'complete' || skippedSetupDone) ? null : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" title="Founder has not yet submitted the onboarding form">
                                  <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-1.5"></span>
                                  Onboarding pending
                                </span>
                                // status.onboardingStatus === 'skipped' || status.onboardingStatus === 'incomplete' ? (
                                //   <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" title="Onboarding form not completed">
                                //     <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-1.5"></span>
                                //     Onboarding incomplete
                                //   </span>
                                // ) : (
                                //   <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" title="Founder has not yet submitted the onboarding form">
                                //     <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-1.5"></span>
                                //     Onboarding pending
                                //   </span>
                                // )
                              );
                              const taskBadge = status.formationTasksTotal > 0 && status.formationTasksRemaining > 0 ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800" title="Formation/registration tasks not yet completed">
                                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-1.5"></span>
                                  Tasks pending
                                </span>
                              ) : null;
                              const grantBadge = status.newGrantsCount > 0 ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" title="New grant application(s) initiated by client">
                                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5"></span>
                                  New grant applications
                                </span>
                              ) : null;
                              return (
                                <div className="flex flex-col gap-1">
                                  {onboardingBadge}
                                  {taskBadge}
                                  {grantBadge}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => handleSelectCompany(company.name)}
                                className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors duration-200"
                              >
                                Select
                              </button>
                              {userInfo?.accountType === 'superadmin' && (
                                <>
                                  <button 
                                    onClick={() => handleEditCompany(company)}
                                    className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors duration-200"
                                  >
                                    Edit
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteCompany(company.name)}
                                    className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors duration-200"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}

        {/* Deleted Companies Tab */}
        {activeTab === 'deleted-companies' && userInfo?.accountType === 'superadmin' && (
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Deleted Companies</h3>
                  <p className="text-sm text-gray-600 mt-1">View and manage deleted companies. You can restore or permanently delete them.</p>
                </div>
              </div>

              {deletedCompanies.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">No deleted companies</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deleted At</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deleted By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {deletedCompanies.map((company) => (
                        <tr key={company.name} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{company.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {company.deletedAt ? new Date(company.deletedAt).toLocaleString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {company.deletedBy || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => handleRestoreCompany(company.name)}
                                className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors duration-200"
                                title="Restore this company"
                              >
                                Restore
                              </button>
                              <button 
                                onClick={() => handlePermanentlyDeleteCompany(company.name)}
                                className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors duration-200"
                                title="Permanently delete this company and all its data"
                                disabled={loading}
                              >
                                {loading ? 'Deleting...' : 'Permanent Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'user-management' && (
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Company</h3>
                  <div className="max-w-md">
                    <select
                      value={selectedCompanyForUsers}
                      onChange={(e) => setSelectedCompanyForUsers(e.target.value)}
                      className="input-field"
                    >
                      <option value="">Select a company to manage users</option>
                      {companies.map((company) => (
                        <option key={company.name} value={company.name}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    {userInfo?.accountType === 'client' && (
                      <p className="text-sm text-gray-600 mt-2">
                        As a super client, you can manage users for your company.
                      </p>
                    )}
                  </div>
                </div>
                {userInfo?.accountType === 'superadmin' && (
                  <div>
                    <button 
                      onClick={() => setShowResetModal(true)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                    >
                      Reset User Accounts
                    </button>
                    <p className="text-xs text-gray-500 mt-1 max-w-xs">
                      Testing only: Reset user accounts to allow re-registration with same emails
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {selectedCompanyForUsers && (
              <CompanyUserManagement 
                userInfo={userInfo} 
                companyName={selectedCompanyForUsers} 
              />
            )}
          </div>
        )}

        {/* Admin Management Tab (Super Admin Only) */}
        {activeTab === 'admins' && userInfo?.accountType === 'superadmin' && (
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Admin Management</h3>
                <p className="text-sm text-gray-600 mt-1">Manage admin users and their company assignments</p>
              </div>
              <button 
                onClick={() => navigate('/create-admin')}
                className="btn-primary"
              >
                Create New Admin
              </button>
            </div>
            
            {/* Pending Invitations Section */}
            {pendingInvitations.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
                <h4 className="text-md font-medium text-gray-900 mb-3">Pending Invitations ({pendingInvitations.length})</h4>
                <div className="space-y-2">
                  {pendingInvitations.map((invitation, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-gray-900">{invitation.email}</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            invitation.accountType === 'superadmin' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {invitation.accountType === 'superadmin' ? 'Super Admin' : 'Admin'}
                          </span>
                          <span className="text-sm text-gray-500">
                            Invited {new Date(invitation.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleResendInvitation(invitation.email)}
                          className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors duration-200"
                        >
                          Resend
                        </button>
                        <button
                          onClick={() => handleDeleteInvitation(invitation.email)}
                          className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors duration-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {admins.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-500">No admin users found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Companies</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {admins.map((admin, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex flex-col">
                            <span>{admin.name || admin.username}</span>
                            <a
                              href={`mailto:${admin.username}`}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                            >
                              {admin.username}
                            </a>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {admin.username !== userInfo?.username ? (
                            <select
                              value={admin.accountType}
                              onChange={(e) => handleRoleChange(admin.username, e.target.value)}
                              className="text-xs font-medium px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="admin">Admin</option>
                              <option value="superadmin">Super Admin</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              admin.accountType === 'superadmin' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {admin.accountType === 'superadmin' ? 'Super Admin' : 'Admin'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(admin.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {admin.assignedCompanies.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {admin.assignedCompanies.map((company, idx) => (
                                <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {company}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">No assignments</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.createdBy}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleEditAdmin(admin)}
                              className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors duration-200"
                            >
                              Edit
                            </button>
                            {admin.username !== userInfo?.username && (
                              <button 
                                onClick={() => handleDeleteAdmin(admin.username)}
                                className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors duration-200"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Template Management Tab (Super Admin Only) */}
        {activeTab === 'templates' && userInfo?.accountType === 'superadmin' && (
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Template Management</h3>
                <p className="text-sm text-gray-600 mt-1">Manage downloadable templates for grant applications, legal documents, and email communications</p>
              </div>

              {/* Template Type Tabs */}
              <div className="mt-4 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setTemplateTab('grant')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      templateTab === 'grant'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Grant Applications
                  </button>
                  <button
                    onClick={() => setTemplateTab('legal')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      templateTab === 'legal'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Legal Documents
                  </button>
                  <button
                    onClick={() => setTemplateTab('email')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      templateTab === 'email'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Email Templates
                  </button>
                </nav>
              </div>
            </div>

            <div className="p-6">
              {/* Grant Templates Tab */}
              {templateTab === 'grant' && (
                <>
                  {templates.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-gray-500">Loading grant templates...</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {templates.map((template, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="text-md font-medium text-gray-900">{template.name}</h4>
                                {template.hasCustomUpload && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Custom
                                  </span>
                                )}
                                {!template.hasCustomUpload && template.hasDefault && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Default
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                              {template.lastUpdated && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Last updated: {new Date(template.lastUpdated).toLocaleDateString()}
                                </p>
                              )}
                              {!template.hasDefault && !template.hasCustomUpload && (
                                <p className="text-xs text-red-500 mt-1">
                                  No template file available - please upload one
                                </p>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              {(template.hasDownload && (template.hasDefault || template.hasCustomUpload)) && (
                                <button
                                  onClick={() => handleTemplateDownload(template.id)}
                                  className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors duration-200"
                                >
                                  Download
                                </button>
                              )}
                              <button
                                onClick={() => setSelectedTemplate(selectedTemplate === template.id ? null : template.id)}
                                className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors duration-200"
                              >
                                {selectedTemplate === template.id ? 'Cancel' : 'Update'}
                              </button>
                            </div>
                          </div>

                          {selectedTemplate === template.id && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select new template file:
                                  </label>
                                  <input
                                    type="file"
                                    onChange={(e) => setTemplateFile(e.target.files[0])}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    {template.hasCustomUpload
                                      ? 'This will replace the current custom template'
                                      : template.hasDefault
                                        ? 'This will override the default template with your custom version'
                                        : 'Upload a template file for this document type'
                                    }
                                  </p>
                                </div>

                                {templateFile && (
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => {
                                        setSelectedTemplate(null);
                                        setTemplateFile(null);
                                      }}
                                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                      disabled={uploadingTemplate}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handleTemplateUpload(template.id, template.name, templateFile)}
                                      disabled={uploadingTemplate}
                                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {uploadingTemplate ? 'Uploading...' : 'Upload Template'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Legal Templates Tab */}
              {templateTab === 'legal' && (
                <>
                  {legalTemplates.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-gray-500">Loading legal templates...</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {legalTemplates.map((template, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="text-md font-medium text-gray-900">{template.name}</h4>
                                {template.hasCustomUpload && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Custom
                                  </span>
                                )}
                                {!template.hasCustomUpload && template.hasDefault && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Default
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                              {template.lastUpdated && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Last updated: {new Date(template.lastUpdated).toLocaleDateString()}
                                </p>
                              )}
                              {!template.hasDefault && !template.hasCustomUpload && (
                                <p className="text-xs text-red-500 mt-1">
                                  No template file available - please upload one
                                </p>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              {(template.hasDownload && (template.hasDefault || template.hasCustomUpload)) && (
                                <button
                                  onClick={() => handleLegalTemplateDownload(template.id)}
                                  className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors duration-200"
                                >
                                  Download
                                </button>
                              )}
                              <button
                                onClick={() => setSelectedLegalTemplate(selectedLegalTemplate === template.id ? null : template.id)}
                                className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors duration-200"
                              >
                                {selectedLegalTemplate === template.id ? 'Cancel' : 'Update'}
                              </button>
                            </div>
                          </div>

                          {selectedLegalTemplate === template.id && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select new legal template file:
                                  </label>
                                  <input
                                    type="file"
                                    onChange={(e) => setLegalTemplateFile(e.target.files[0])}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    {template.hasCustomUpload
                                      ? 'This will replace the current custom legal template'
                                      : template.hasDefault
                                        ? 'This will override the default legal template with your custom version'
                                        : 'Upload a legal template file for this document type'
                                    }
                                  </p>
                                </div>

                                {legalTemplateFile && (
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => {
                                        setSelectedLegalTemplate(null);
                                        setLegalTemplateFile(null);
                                      }}
                                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                      disabled={uploadingLegalTemplate}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handleLegalTemplateUpload(template.id, template.name, legalTemplateFile)}
                                      disabled={uploadingLegalTemplate}
                                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {uploadingLegalTemplate ? 'Uploading...' : 'Upload Legal Template'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Email Templates Tab */}
              {templateTab === 'email' && (
                <>
                  {emailTemplates.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-gray-500">Loading email templates...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> Email templates use placeholder variables like {'{grantTitle}'}, {'{companyName}'}, etc.
                          These will be automatically replaced with actual values when emails are sent.
                        </p>
                      </div>

                      {/* Group templates by category */}
                      {Object.entries(
                        emailTemplates.reduce((groups, template) => {
                          const category = template.category || 'Uncategorized';
                          if (!groups[category]) groups[category] = [];
                          groups[category].push(template);
                          return groups;
                        }, {})
                      ).map(([category, categoryTemplates]) => {
                        const isCategoryCollapsed = collapsedCategories.has(category);
                        const toggleCategory = () => {
                          setCollapsedCategories(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(category)) {
                              newSet.delete(category);
                            } else {
                              newSet.add(category);
                            }
                            return newSet;
                          });
                        };

                        return (
                        <div key={category} className="border border-gray-300 rounded-lg overflow-hidden">
                          <button
                            onClick={toggleCategory}
                            className="w-full bg-gray-100 px-4 py-3 border-b border-gray-300 hover:bg-gray-200 transition-colors flex items-center justify-between"
                          >
                            <h4 className="text-md font-semibold text-gray-900">{category}</h4>
                            <svg
                              className={`w-5 h-5 text-gray-600 transition-transform ${isCategoryCollapsed ? '-rotate-90' : 'rotate-0'}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {!isCategoryCollapsed && (
                          <div className="p-4 space-y-4">
                            {categoryTemplates.map((template, index) => {
                        return (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="text-md font-medium text-gray-900">{template.name}</h4>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  {template.category}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                                
                              {selectedEmailTemplate === template.id && editingEmailTemplate ? (
                                <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Subject Line:
                                    </label>
                                    <input
                                      type="text"
                                      value={emailTemplateContent.subject || template.subject}
                                      onChange={(e) => setEmailTemplateContent(prev => ({
                                        ...prev,
                                        subject: e.target.value
                                      }))}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="Email subject line..."
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Email Content (HTML):
                                    </label>
                                    <textarea
                                      value={emailTemplateContent.htmlContent || template.htmlContent}
                                      onChange={(e) => setEmailTemplateContent(prev => ({
                                        ...prev,
                                        htmlContent: e.target.value
                                      }))}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                      rows="8"
                                      placeholder="Email HTML content..."
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      Use HTML tags for formatting. Available variables: {'{grantTitle}'}, {'{companyName}'}, {'{contactName}'}, etc.
                                    </p>
                                  </div>

                                  <div className="flex justify-end space-x-2 pt-2">
                                    <button
                                      onClick={() => {
                                        setSelectedEmailTemplate(null);
                                        setEditingEmailTemplate(false);
                                        setEmailTemplateContent({ subject: '', htmlContent: '' });
                                      }}
                                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={async () => {
                                        try {
                                          // Save template to database
                                          const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/email-templates/${template.id}`, {
                                            method: 'PUT',
                                            credentials: 'include',
                                            headers: {
                                              'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({
                                              subject: emailTemplateContent.subject || template.subject,
                                              html_content: emailTemplateContent.htmlContent || template.htmlContent,
                                              name: template.name,
                                              description: template.description
                                            })
                                          });

                                          if (response.ok) {
                                            // Update local state
                                            const updatedTemplates = emailTemplates.map(t =>
                                              t.id === template.id
                                                ? {
                                                    ...t,
                                                    subject: emailTemplateContent.subject || t.subject,
                                                    htmlContent: emailTemplateContent.htmlContent || t.htmlContent
                                                  }
                                                : t
                                            );
                                            setEmailTemplates(updatedTemplates);
                                            setSuccess(`Email template "${template.name}" updated successfully!`);
                                            setSelectedEmailTemplate(null);
                                            setEditingEmailTemplate(false);
                                            setEmailTemplateContent({ subject: '', htmlContent: '' });
                                            setTimeout(() => setSuccess(''), 3000);
                                          } else {
                                            const errorData = await response.json();
                                            setError(`Failed to update template: ${errorData.error || 'Unknown error'}`);
                                            setTimeout(() => setError(''), 5000);
                                          }
                                        } catch (error) {
                                          console.error('Error updating email template:', error);
                                          setError('Failed to update email template. Please try again.');
                                          setTimeout(() => setError(''), 5000);
                                        }
                                      }}
                                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                                    >
                                      Save Changes
                                    </button>
                                  </div>
                                </div>

                              ) : (
                                <div className="mt-2 space-y-2">
                                  <div className="text-xs text-gray-600">
                                    <strong>Subject:</strong> {template.subject}
                                  </div>
                                  <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                    <strong>Content Preview:</strong>
                                    <div className="mt-1" dangerouslySetInnerHTML={{ __html: template.htmlContent }} />
                                  </div>
                                </div>
                              )}
                                </div>
                            </div>

                            <div className="ml-4">
                              {selectedEmailTemplate === template.id && editingEmailTemplate ? null : (
                                <button
                                  onClick={() => {
                                    setSelectedEmailTemplate(template.id);
                                    setEditingEmailTemplate(true);
                                    setEmailTemplateContent({
                                      subject: template.subject,
                                      htmlContent: template.htmlContent
                                    });
                                  }}
                                  className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors duration-200"
                                >
                                  Edit Template
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                          </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}


        {/* Edit Admin Assignments Modal */}
        {showEditAssignments && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Edit Company Assignments for {showEditAssignments}
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {companies.length === 0 ? (
                    <p className="text-sm text-gray-500">No companies available</p>
                  ) : (
                    companies.map((company) => (
                      <label key={company.name} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingAssignments.includes(company.name)}
                          onChange={() => toggleCompanyAssignment(company.name)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{company.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEditAssignments(null);
                      setEditingAssignments([]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAssignments}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset User Accounts Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[32rem] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 17.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  Reset User Accounts
                </h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-700 mb-2">
                    <strong>⚠️ DANGER:</strong> This will permanently delete all user data for the specified email addresses.
                  </p>
                  <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                    <li>User credentials from database</li>
                    <li>Email verification tokens</li>
                    <li>User account metadata</li>
                  </ul>
                  <p className="text-sm text-red-700 mt-2">
                    <strong>Use only for testing purposes.</strong> This action cannot be undone.
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email addresses (one per line):
                  </label>
                  <textarea
                    value={resetEmails}
                    onChange={(e) => setResetEmails(e.target.value)}
                    placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                    className="input-field w-full h-32 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter one email address per line. These accounts will be completely removed from the system.
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowResetModal(false);
                      setResetEmails('');
                    }}
                    disabled={resetLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetUserAccounts}
                    disabled={resetLoading || !resetEmails.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resetLoading ? 'Resetting...' : 'Reset Accounts'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Admin Modal */}
        {editingAdmin && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[32rem] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Edit Admin: {editAdminData.username}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={editAdminData.name}
                      onChange={(e) => setEditAdminData({ ...editAdminData, name: e.target.value })}
                      className="input-field w-full"
                      placeholder="Enter admin full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username (Email)
                    </label>
                    <input
                      type="text"
                      value={editAdminData.username}
                      disabled
                      className="input-field w-full bg-gray-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Company Assignments
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {companies.length === 0 ? (
                        <p className="text-sm text-gray-500">No companies available</p>
                      ) : (
                        companies.map((company) => (
                          <label key={company.name} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingAssignments.includes(company.name)}
                              onChange={() => {
                                setEditingAssignments(prev => {
                                  if (prev.includes(company.name)) {
                                    return prev.filter(c => c !== company.name);
                                  } else {
                                    return [...prev, company.name];
                                  }
                                });
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{company.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Select companies this admin can manage. Leave empty for no access to any companies.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={handleCancelEditAdmin}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAdmin}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Management — one task across many companies, or a single company's
            own Task Settings page shown here so an admin doesn't have to switch
            into that company first. */}
        {activeTab === 'task-management' && (
          <div className="space-y-6">
            <div className="flex gap-2">
              <button
                onClick={() => setTaskManagementView('all-companies')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  taskManagementView === 'all-companies'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Companies
              </button>
              <button
                onClick={() => setTaskManagementView('by-company')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  taskManagementView === 'by-company'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                By Company
              </button>
            </div>

            {taskManagementView === 'all-companies' && (
              <BulkTaskManagement companies={companies} />
            )}

            {taskManagementView === 'by-company' && (
              <div className="space-y-6">
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Company</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Task settings are read and written against the company you are working in,
                    so picking one here switches your active company — the same thing that
                    happens when you open a company from the Companies tab.
                  </p>
                  <div className="max-w-md">
                    <select
                      value={selectedCompanyForTasks}
                      onChange={(e) => handleSelectCompanyForTasks(e.target.value)}
                      disabled={switchingTaskCompany}
                      className="input-field"
                    >
                      <option value="">Select a company to manage tasks</option>
                      {companies.map((company) => (
                        <option key={company.name} value={company.name}>
                          {company.officialName || company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {switchingTaskCompany && (
                    <p className="text-sm text-gray-500 mt-2">Switching company...</p>
                  )}
                  {taskCompanyError && (
                    <p className="text-sm text-red-600 mt-2">{taskCompanyError}</p>
                  )}
                </div>

                {selectedCompanyForTasks && !switchingTaskCompany && (
                  <div className="card p-6">
                    {/* Remounted per company so every request it makes is issued
                        after the company context has been switched. */}
                    <TaskSettings
                      key={selectedCompanyForTasks}
                      userInfo={{ ...userInfo, selectedCompany: selectedCompanyForTasks }}
                      embedded
                      companyLabel={
                        companies.find(c => c.name === selectedCompanyForTasks)?.officialName
                        || selectedCompanyForTasks
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Investor Management Tab */}
        {activeTab === 'investors' && (
          <div className="card p-6 space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Investor Management</h3>
              <button
                onClick={() => setShowInviteModal(true)}
                className="btn-primary"
              >
                Invite Investor
              </button>
            </div>

            <div className="flex gap-2 border-b border-gray-200 pb-3">
              <button
                onClick={() => setInvestorSubTab('requests')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                  investorSubTab === 'requests'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Requests & Investors
              </button>
              <button
                onClick={() => setInvestorSubTab('notifications')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                  investorSubTab === 'notifications'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Notification Settings
              </button>
              <button
                onClick={() => setInvestorSubTab('nda')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                  investorSubTab === 'nda'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                NDA Tracking
              </button>
            </div>

            {investorLoading ? (
              <div className="text-center py-4">
                <p className="text-gray-500 text-sm">Loading investor data...</p>
              </div>
            ) : (
              <>
                {/* Requests & Investors Sub-Tab */}
                {investorSubTab === 'requests' && (
                  <>
                    {/* Pending Requests */}
                    {investorRequests.filter(r => r.status === 'pending').length > 0 && (
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">Pending Requests</h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {investorRequests.filter(r => r.status === 'pending').map(req => (
                                <tr key={req.id}>
                                  <td className="px-4 py-3 text-sm text-gray-900">{req.name}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{req.email}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{req.company || '-'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(req.requestedAt).toLocaleDateString()}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <button
                                      onClick={() => handleApproveInvestor(req.id)}
                                      className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded mr-2"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleRejectInvestor(req.id)}
                                      className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded"
                                    >
                                      Reject
                                    </button>
                                    <button
                                      onClick={() => handleDeleteInvestor(req.id, req.name)}
                                      className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded ml-2"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Approved Investors */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-3">Approved Investors</h4>
                      {investorRequests.filter(r => r.status === 'approved').length === 0 ? (
                        <p className="text-sm text-gray-500">No approved investors yet.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approved</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approved By</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {investorRequests.filter(r => r.status === 'approved').map(req => (
                                <tr key={req.id}>
                                  <td className="px-4 py-3 text-sm text-gray-900">{req.name}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{req.email}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{req.company || '-'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-500">{req.approvedAt ? new Date(req.approvedAt).toLocaleDateString() : '-'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-500">{req.approvedBy || '-'}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <button
                                      onClick={() => handleDeleteInvestor(req.id, req.name)}
                                      className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Notification Settings Sub-Tab */}
                {investorSubTab === 'notifications' && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-2">Email Notification Settings</h4>
                    <p className="text-sm text-gray-500 mb-3">Designate which admins receive email notifications when new investor access requests are submitted.</p>
                    {admins.length === 0 ? (
                      <p className="text-sm text-gray-500">No admins available.</p>
                    ) : (
                      <div className="space-y-2">
                        {admins.map(admin => {
                          const setting = notifySettings.find(s => s.adminEmail === (admin.email || admin.username));
                          const isEnabled = setting ? setting.enabled === 'true' || setting.enabled === true : false;
                          return (
                            <div key={admin.email || admin.username} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                              <div>
                                <span className="text-sm font-medium text-gray-900">{admin.name || admin.username}</span>
                                <span className="text-sm text-gray-500 ml-2">({admin.email || admin.username})</span>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={() => handleToggleNotify(admin.email || admin.username, isEnabled)}
                                  className="sr-only peer"
                                />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                                <span className="ml-2 text-xs text-gray-600">{isEnabled ? 'On' : 'Off'}</span>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* NDA Tracking Sub-Tab */}
                {investorSubTab === 'nda' && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-2">NDA Signed Records</h4>
                    <p className="text-sm text-gray-500 mb-3">Shows which companies each investor has signed an NDA for, granting access to confidential documents.</p>

                    {/* Filters */}
                    <div className="flex items-center gap-3 mb-4">
                      <select
                        value={ndaFilterField}
                        onChange={(e) => { setNdaFilterField(e.target.value); setNdaFilterValue(''); }}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Fields</option>
                        <option value="companyName">Client Company</option>
                        <option value="investorCompany">Investor Company</option>
                        <option value="investorEmail">Investor Email</option>
                        <option value="investorName">Investor Name</option>
                      </select>
                      <input
                        type="text"
                        placeholder={ndaFilterField === 'all' ? 'Search all fields...' : `Filter by ${ndaFilterField === 'companyName' ? 'client company' : ndaFilterField === 'investorCompany' ? 'investor company' : ndaFilterField === 'investorEmail' ? 'investor email' : 'investor name'}...`}
                        value={ndaFilterValue}
                        onChange={(e) => setNdaFilterValue(e.target.value)}
                        className="flex-1 max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {ndaRecords.length === 0 ? (
                      <p className="text-sm text-gray-500">No NDAs signed yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor Name</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor Email</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor Company</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Company</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signed Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {ndaRecords
                              .filter(nda => {
                                if (!ndaFilterValue) return true;
                                const search = ndaFilterValue.toLowerCase();
                                if (ndaFilterField === 'all') {
                                  return (
                                    (nda.investorEmail || '').toLowerCase().includes(search) ||
                                    (nda.investorName || '').toLowerCase().includes(search) ||
                                    (nda.investorCompany || '').toLowerCase().includes(search) ||
                                    (nda.companyName || '').toLowerCase().includes(search)
                                  );
                                }
                                return ((nda[ndaFilterField] || '').toLowerCase().includes(search));
                              })
                              .map((nda, idx) => (
                              <tr key={nda.id || idx}>
                                <td className="px-4 py-3 text-sm text-gray-900">{nda.investorName || '-'}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{nda.investorEmail}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{nda.investorCompany || '-'}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{nda.companyName}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">{nda.signedAt ? new Date(nda.signedAt).toLocaleDateString() : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Invite Investor Modal */}
            {showInviteModal && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-lg">
                  <h3 className="text-lg font-semibold mb-4">Invite Investor</h3>
                  <form onSubmit={handleInviteInvestor} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        required
                        value={inviteForm.name}
                        onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                        className="input-field mt-1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <input
                        type="text"
                        value={inviteForm.company}
                        onChange={(e) => setInviteForm({ ...inviteForm, company: e.target.value })}
                        className="input-field mt-1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        className="input-field mt-1"
                      />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        type="button"
                        onClick={() => { setShowInviteModal(false); setInviteForm({ name: '', company: '', email: '' }); }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={inviteLoading}
                        className="btn-primary disabled:opacity-50"
                      >
                        {inviteLoading ? 'Sending...' : 'Send Invitation'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit Company Modal */}
        {editingCompany && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[32rem] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Edit Company: {companies.find(c => c.name === editingCompany)?.officialName || editingCompany}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name:
                    </label>
                    <input
                      type="text"
                      value={editCompanyData.name}
                      onChange={(e) => setEditCompanyData(prev => ({ ...prev, name: e.target.value }))}
                      className="input-field w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Admins/SuperAdmins:
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {admins.length === 0 ? (
                        <p className="text-sm text-gray-500">No admins available</p>
                      ) : (
                        admins.map((admin) => (
                          <label key={admin.username} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editCompanyData.assignedAdmins.includes(admin.username)}
                              onChange={() => toggleAdminAssignment(admin.username)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              {admin.name || admin.username} {admin.accountType === 'superadmin' && '(Super Admin)'}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setEditingCompany(null);
                      setEditCompanyData({ name: '', assignedAdmins: [] });
                    }}
                    disabled={savingCompanyEdit}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveCompanyEdit}
                    disabled={savingCompanyEdit}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingCompanyEdit ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Confirmation Modal */}
        <EmailConfirmationModal
          isOpen={emailConfirmation.isOpen}
          onClose={() => setEmailConfirmation({ isOpen: false, emailData: null, onConfirm: null })}
          onConfirm={emailConfirmation.onConfirm}
          emailData={emailConfirmation.emailData}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default CompanyManagement;